import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

const paperSx = {
  p: 2,
  mt: 1.5,
  borderRadius: 2,
  border: '1px solid rgba(25,118,210,0.1)',
  background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)',
};

function streakChip(n) {
  if (!n || n < 2) return null;
  return (
    <Chip
      size="small"
      color={n >= 3 ? 'error' : 'warning'}
      label={`${n} meses seguidos`}
      sx={{ fontWeight: 600 }}
    />
  );
}

function RankingBar({ ranking = [], title }) {
  const data = useMemo(
    () =>
      (ranking || []).slice(0, 12).map((r) => ({
        name: r.name.length > 28 ? `${r.name.slice(0, 26)}…` : r.name,
        total: r.total,
      })),
    [ranking]
  );
  if (!data.length) return null;
  return (
    <Paper elevation={0} sx={{ ...paperSx, mb: 2 }}>
      {title && (
        <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 1 }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: '100%', height: Math.max(220, 40 + data.length * 28) }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="total" fill="#1976d2" radius={[0, 4, 4, 0]} name="Ocorrências" />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

function RankingTable({ ranking = [], showQuestions = false }) {
  if (!ranking.length) return null;
  return (
    <Paper elevation={0} sx={{ overflowX: 'auto', border: '1px solid rgba(25,118,210,0.12)', mb: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Item / pessoa</TableCell>
            <TableCell align="right">Ocorrências</TableCell>
            <TableCell>Sequência</TableCell>
            <TableCell>Última data</TableCell>
            {showQuestions && <TableCell>Perguntas</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {ranking.map((r) => (
            <TableRow key={`${r.tipoOpcoes}-${r.name}`} hover>
              <TableCell>
                <Typography fontWeight={600}>{r.name}</Typography>
                {r.tipoOpcoes && (
                  <Typography variant="caption" color="text.secondary">
                    {r.tipoOpcoes}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">{r.total}</TableCell>
              <TableCell>{streakChip(r.consecutiveMonths) || '—'}</TableCell>
              <TableCell>{r.lastDate || '—'}</TableCell>
              {showQuestions && (
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {(r.questions || []).join(' · ') || '—'}
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

/**
 * recurrence: { byQuestion: [...], consolidated: [...] }
 * mode: 'template' | 'overview'
 */
export default function SafetyRecurrencePanel({ recurrence, mode = 'template' }) {
  const byQuestion = recurrence?.byQuestion || [];
  const consolidated = recurrence?.consolidated || [];

  if (!byQuestion.length && !consolidated.length) return null;

  return (
    <Box sx={{ mt: 3, mb: 2 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Recorrência por pessoa/item
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Automático para Seleção múltipla com opções de setores, pessoas ou contratos
        {mode === 'overview' ? ' (consolidado entre formulários do seu escopo)' : ''}.
      </Typography>

      {mode === 'overview' && consolidated.length > 0 && (
        <>
          <RankingBar ranking={consolidated} title="Ranking consolidado" />
          <RankingTable ranking={consolidated.slice(0, 30)} showQuestions />
        </>
      )}

      {mode === 'template' &&
        byQuestion.map((block) => (
          <Box key={block.questionId} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {block.label}{' '}
              <Chip size="small" label={block.tipoOpcoes} sx={{ ml: 0.5 }} />
            </Typography>
            <RankingBar ranking={block.ranking} />
            <RankingTable ranking={block.ranking} />
          </Box>
        ))}

      {mode === 'template' && consolidated.length > 1 && byQuestion.length > 1 && (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Consolidado deste formulário
          </Typography>
          <RankingBar ranking={consolidated} />
          <RankingTable ranking={consolidated} />
        </>
      )}
    </Box>
  );
}
